<template>
    <div class="row q-col-gutter-md">
        <div
            v-if="allMedias.length > 0"
            class="col-12 text-secondary text-body2"
        >
            <b>{{ $t('Attached Photos') }}</b>
            <br />
            {{
                $t('Maximum of {max} files allowed.', {
                    max: MAX_MEDIA_COUNT
                })
            }}
            <br />
            {{ $t('Supported file types: PNG, JPG, and JPEG.') }}
        </div>
        <div v-if="exceedsMaxMediaThreshold" class="col-12">
            <Banner
                :is-visible="exceedsMaxMediaThreshold"
                type="warning"
                :message="
                    $t(
                        'The maximum number of media ({max}) has been exceeded.',
                        {
                            max: MAX_MEDIA_COUNT
                        }
                    )
                "
            />
        </div>
        <div
            :class="getColClass"
            v-for="(media, index) in allMedias"
            :key="index"
            style="position: relative"
        >
            <q-btn
                color="negative"
                @click="removeImage(index, media['id'])"
                icon="fas fa-times"
                class="btn-remove-image"
                round
                dense
            />
            <div v-if="!isLoading" class="image-container">
                <q-img :src="media.url" />
            </div>
            <q-spinner v-else color="primary" size="md" />
        </div>
    </div>
</template>

<script>
import { loadPhaseMedia } from '@utils/mediaManager.js';
import Banner from '@components/Banner.vue';
import { MAX_MEDIA_COUNT } from '@utils/mediaManager.js';

export default {
    name: 'MediaGridManager',
    components: {
        Banner
    },
    emits: ['update:removeExistingMedia', 'update:removeNewMedia'],
    props: {
        existingMedias: {
            type: Array,
            required: false,
            default: () => []
        },
        alternativeLayout: {
            type: Boolean,
            default: false
        },
        newMedias: {
            type: Array,
            required: false,
            default: () => []
        },
        phaseId: {
            type: Number,
            required: false,
            default: null
        }
    },
    created() {
        if (this.phaseId) {
            this.loadMedias();
        }
    },
    watch: {
        existingMedias: {
            handler(existingMedias) {
                this.internalMedias = [...existingMedias];
            },
            deep: true
        },
        newMedias: {
            handler(newMedias) {
                this.newInternalMedias = [...newMedias];
            },
            deep: true
        }
    },
    data() {
        return {
            internalMedias: [],
            newInternalMedias: [],
            isLoading: false,
            MAX_MEDIA_COUNT
        };
    },
    computed: {
        /**
         * Returns all media, including new and existing media.
         * @returns {Array} - The array of media objects.
         */
        allMedias() {
            return [...this.internalMedias, ...this.newInternalMedias];
        },
        /**
         * Returns the column class based on the screen size.
         * @returns {Object} - The column class object.
         */
        getColClass() {
            if (this.alternativeLayout) {
                return {
                    'col-3': this.$q.screen.gt.xs,
                    'col-6': this.$q.screen.lt.md,
                    'col-12': this.$q.screen.lt.sm
                };
            }
            return {
                'col-4': this.$q.screen.gt.xs,
                'col-6': this.$q.screen.lt.md,
                'col-12': this.$q.screen.lt.sm
            };
        },
        /**
         * Returns true if the number of media exceeds the maximum allowed.
         * @returns {boolean} - True if the number of media exceeds the maximum allowed, false otherwise.
         */
        exceedsMaxMediaThreshold() {
            return this.allMedias.length > this.MAX_MEDIA_COUNT;
        }
    },
    methods: {
        loadPhaseMedia,
        /**
         * Removes an image from the list.
         * If the media has an ID, it means that this media exists in the database and must be removed using an API call.
         * If it doesn't have an ID, it means that this is a new media and must be removed from the `add` object in the parent component.
         * @param {number} index - The index of the image to remove.
         * @param {number} mediaId - The ID of the media to remove.
         */
        removeImage(index, mediaId) {
            if (mediaId) {
                const removedMedia = this.internalMedias.splice(index, 1)[0];
                this.$emit('update:removeExistingMedia', removedMedia['id']);
            } else {
                const indexToRemove = index - this.internalMedias.length;
                this.newInternalMedias.splice(indexToRemove, 1);
                this.$emit('update:removeNewMedia', this.newInternalMedias);
            }
        },
        /**
         * Load media from the API.
         */
        async loadMedias() {
            try {
                this.isLoading = true;
                const medias = await this.loadPhaseMedia(this.phaseId);
                this.internalMedias = medias;
            } catch (error) {
                this.$notifyError(this.$t('Failed to load media.'), error);
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.image-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    overflow: hidden;
    background-color: $lighter-grey;
    border: 1px solid $light-grey;
}

.btn-remove-image {
    position: absolute;
    top: 10px;
    right: -10px;
    z-index: 1000;
}
</style>
